import React, { useEffect, useMemo, useState } from "react";
import { NavItemProps } from "@src/components/modules/Nav/NavItemProps";
import { createContext } from "react";
import { useLocation } from "react-router-dom";
import {
  BudgetIcon,
  DocumentIcon,
  DollarIcon,
  HomeIcon,
  PendingIcon,
  PeopleIcon,
  PersonOutlineIcon,
  SearchIcon,
  SettingsIcon,
  VendorsIcon,
} from "@src/components/elements/assets";
import { CreditCardIcon } from "@src/components/elements/assets/CreditCardIcon";
import { Page, pathIsPage } from "@src/shared/pages";
import { HeadCountIcon } from "@src/components/elements/assets/HeadCountIcon";
import { useSession } from "@src/contexts/SessionContext";
import abilitiesFor from "@src/services/ability";
import { useFeatureFlags } from "@src/hooks/useFeatureFlag";
import { useAuth } from "@src/contexts/AuthProvider";
import _ from "lodash";
import { PlusIcon } from "@src/components/elements/assets/PlusIcon";
import { PurchaseOrderIcon } from "@src/components/elements/assets/PurchaseOrderIcon";
import { ReportsIcon } from "@src/components/elements/assets/ReportsIcon";
import { SourcingIcon } from "@src/components/elements/assets/SourcingIcon";

export const navContext = createContext<{
  navItems: NavItemProps[];
  path: string;
  onMenuToggle: (idx: number) => void;
  showSearch: boolean;
  setShowSearch: (show: boolean) => void;
}>({
  navItems: [],
  path: "",
  onMenuToggle: _.noop,
  showSearch: false,
  setShowSearch: _.noop,
});

export const NavProvider: React.FC = ({ children }) => {
  const path = useLocation().pathname;
  const pathWithoutTrailingSlash = path.replace(/\/$/, "");
  const isVendorPortal = path.startsWith("/v/");

  const { tokenUserData } = useAuth();
  const {
    hasErpClassifications,
    paymentsEnabled,
    enablePaymentAccounting,
    enableSpendRequests,
    enableIntakeRequests,
    enableSourcing,
    hasSpendRequestAccess,
    enableVendorIntake,
    hasVendorAdminAccess,
    hasAdminAccess,
    hasUserAdminAccess,
    hasSuperViewerAccess,
    enableNetsuitePurchaseOrder,
    hasFinancialAnalysisAccess,
    currentCompany,
    isRequestCategoryOwner,
  } = useSession();

  const [showSearch, setShowSearch] = useState(false);

  const ability = useMemo(() => abilitiesFor(tokenUserData), [tokenUserData]);
  const { getFlag } = useFeatureFlags();

  const hasPaymentsSupport =
    (tokenUserData?.hasPaymentsAccess || tokenUserData?.hasAdminAccess) &&
    paymentsEnabled;
  const hasPaymentsSettingsAccess =
    hasPaymentsSupport && ability.can("manage", "Settings");
  const hasHeadcountSupport = getFlag("headcount");
  const canSeeFinancialAnalysis =
    hasAdminAccess ||
    (!!currentCompany?.isErpLinked && hasFinancialAnalysisAccess);
  const [navItems, setNavItems] = useState<NavItemProps[]>([]);

  useEffect(() => {
    if (isVendorPortal) {
      setNavItems([
        {
          type: "link",
          activePages: [Page.VENDOR_PORTAL_HOME],
          display: (
            <>
              <HomeIcon />
              <span>Home</span>
            </>
          ),
          to: Page.VENDOR_PORTAL_HOME,
        },
        {
          type: "link",
          activePages: [Page.VENDOR_PORTAL_PAYMENTS],
          display: (
            <>
              <CreditCardIcon />
              <span>Payments</span>
            </>
          ),
          to: Page.VENDOR_PORTAL_PAYMENTS,
        },
        {
          type: "link",
          activePages: [
            Page.VENDOR_PORTAL_FORMS,
            Page.VENDOR_PORTAL_FORM_EDIT,
            Page.VENDOR_PORTAL_FORM_DETAILS,
          ],
          display: (
            <>
              <PendingIcon />
              <span>Forms</span>
            </>
          ),
          to: Page.VENDOR_PORTAL_FORMS,
        },
        {
          type: "link",
          activePages: [Page.VENDOR_PORTAL_CUSTOMERS],
          display: (
            <>
              <PeopleIcon />
              <span>Customers</span>
            </>
          ),
          to: Page.VENDOR_PORTAL_CUSTOMERS,
        },
        {
          type: "link",
          activePages: [Page.VENDOR_PORTAL_MEMBERS],
          display: (
            <>
              <PersonOutlineIcon />
              <span>Members</span>
            </>
          ),
          to: Page.VENDOR_PORTAL_MEMBERS,
        },

        {
          type: "link",
          activePages: [],
          display: (
            <>
              <SettingsIcon />
              <span>Company profile</span>
            </>
          ),
          to: Page.VENDOR_PORTAL_COMPANY_PROFILE,
        },
      ]);
    } else {
      setNavItems([
        {
          type: "link",
          display: (
            <>
              <HomeIcon />
              <span>Home</span>
            </>
          ),
          activePages: [Page.HOME, Page.DEFAULT],
          to: Page.HOME,
        },
        {
          type: "function" as const,
          to: () => setShowSearch(!showSearch),
          canShow: hasAdminAccess || hasSuperViewerAccess,
          display: (
            <>
              <SearchIcon />
              <span>Search</span>
            </>
          ),
          activePages: [],
        },
        {
          type: "link" as const,
          display: (
            <div className="row">
              <PendingIcon />
              <span>Requests</span>
            </div>
          ),
          activePages: [
            Page.INTAKE_REQUESTS,
            Page.INTAKE_REQUEST_DETAILS,
            Page.INTAKE_REQUEST_DETAILS_TAB,
            Page.INTAKE_REQUESTS_FOR_CATEGORY,
          ],
          to: Page.INTAKE_REQUESTS,
          canShow: enableIntakeRequests,
          sibling: {
            display: <PlusIcon fill="white" />,
            to: Page.NEW_INTAKE_REQUEST_SELECTION,
          },
        },
        {
          type: "link" as const,
          display: (
            <>
              <DollarIcon />
              <span>Spend requests</span>
            </>
          ),
          canShow:
            (enableSpendRequests && !enableIntakeRequests) ||
            (enableSpendRequests && hasSpendRequestAccess) ||
            (enableSpendRequests && hasAdminAccess),
          activePages: [
            Page.SPEND_REQUESTS,
            Page.SPEND_REQUEST_DETAILS,
            Page.ADD_SPEND_REQUEST,
            Page.EDIT_SPEND_REQUEST,
          ],
          to: Page.SPEND_REQUESTS,
        },
        {
          type: "link",
          canShow: enableNetsuitePurchaseOrder,
          display: (
            <>
              <PurchaseOrderIcon />
              <span>Purchase orders</span>
            </>
          ),
          activePages: [Page.PURCHASE_ORDERS, Page.PURCHASE_ORDER_DETAILS],
          to: Page.PURCHASE_ORDERS,
        },
        {
          type: "link",
          canShow: hasAdminAccess || hasSuperViewerAccess,
          display: (
            <>
              <ReportsIcon className="icon-size" /> <span>Reports</span>
            </>
          ),
          activePages: [Page.REPORTS, Page.REQUEST_ANALYTICS],
          to: Page.REPORTS,
        },
        ...(hasPaymentsSupport
          ? [
              {
                type: "menu" as const,
                display: (
                  <>
                    <CreditCardIcon />
                    <span>Payments</span>
                  </>
                ),
                isOpen: hasPaymentsSupport ?? false,
                canShow: paymentsEnabled,
                links: [
                  {
                    type: "link" as const,
                    activePages: [Page.PAYMENTS_INVOICES],
                    display: <span>Inbox</span>,
                    to: Page.PAYMENTS_INVOICES,
                  },
                  {
                    type: "link" as const,
                    activePages: [
                      Page.PAYMENTS,
                      Page.NEW_BILL,
                      Page.NEW_BILL_FROM_INVOICE,
                      Page.BILL_DETAILS,
                      Page.BILL_PAYMENT_DETAILS,
                      Page.BILLS,
                    ],
                    display: <span>Bills & Payments</span>,
                    to: Page.BILLS,
                  },
                  ...(hasPaymentsSupport && enablePaymentAccounting
                    ? [
                        {
                          type: "link" as const,
                          display: <span>Accounting</span>,
                          to: Page.PAYMENTS_TRANSACTIONS,
                          activePages: [
                            Page.PAYMENTS_TRANSACTIONS,
                            Page.SYNCED_LEDGER_ENTRIES,
                            Page.ERRORED_LEDGER_ENTRIES,
                            Page.UNSYNCED_LEDGER_ENTRIES,
                          ],
                        },
                      ]
                    : []),
                ],
              },
            ]
          : []),
        {
          type: "menu",
          isOpen: true,
          display: (
            <>
              <VendorsIcon />
              <span>Vendor management</span>
            </>
          ),
          links: [
            {
              type: "link",
              activePages: [
                Page.VENDORS,
                Page.VENDOR_DETAILS,
                Page.VENDOR_DETAILS_TAB,
                Page.ADD_VENDOR,
                Page.EDIT_VENDOR,
              ],
              to: Page.VENDORS,
              display: <span>Vendors</span>,
            },
            {
              type: "link",
              activePages: [
                Page.SERVICES,
                Page.SERVICE_DETAILS,
                Page.ADD_SERVICE,
                Page.EDIT_SERVICE,
              ],
              display: <span>Services</span>,
              to: Page.SERVICES,
            },
            {
              type: "link",
              activePages: [Page.VENDOR_FORMS, Page.VENDOR_FORM_DETAILS],
              display: <span>Forms</span>,
              to: Page.VENDOR_FORMS,
              canShow: enableVendorIntake,
            },
          ],
        },
        {
          type: "link",
          display: (
            <>
              <SourcingIcon />
              <span>Sourcing</span>
            </>
          ),
          to: Page.SOURCING_FORMS,
          activePages: [
            Page.SOURCING_FORMS,
            Page.SOURCING_FORM_DETAILS,
            Page.SOURCING_FORM_DETAILS_TAB,
          ],
          canShow: enableSourcing,
        },
        {
          type: "menu",
          isOpen: false,
          display: (
            <>
              <BudgetIcon /> <span>Financial analysis</span>
            </>
          ),
          canShow: canSeeFinancialAnalysis,
          links: [
            {
              type: "link",
              activePages: [Page.FINANCIAL_INSIGHTS],
              display: <span>Insights</span>,
              to: Page.FINANCIAL_INSIGHTS,
              canShow: ability.can("read", "Plan"),
            },
            {
              type: "link",
              activePages: [
                Page.PLAN,
                Page.PLANS,
                Page.PLAN_IMPORT,
                Page.PLAN_IMPORT_MAP_BUSINESS_UNITS,
                Page.PLAN_IMPORT_MAP_SPEND_CATEGORIES,
                Page.PLAN_IMPORT_MAP_REVENUE_CATEGORIES,
                Page.PLAN_IMPORT_MAP_DEPARTMENTS,
                Page.PLAN_IMPORT_MAP_LOCATIONS,
                Page.PLAN_IMPORT_MAP_VENDORS,
                Page.PLAN_IMPORT_MAP_BUSINESS_UNITS,
              ],
              display: <span>Plans</span>,
              to: Page.PLANS,
              canShow: ability.can("read", "Plan"),
            },
            {
              type: "link",
              activePages: [
                Page.ACTUALS,
                Page.ACTUALS_LOGIN,
                Page.ACTUALS_SYNC_STATUS,
                Page.ACTUALS_WIZARD,
              ],
              display: <span>Actuals</span>,
              to: Page.ACTUALS,
            },
            {
              type: "link",
              activePages: [Page.TRANSACTIONS],
              display: <span>Transactions</span>,
              to: Page.TRANSACTIONS,
            },
          ],
        },
        {
          type: "menu",
          isOpen: true,
          display: (
            <>
              <HeadCountIcon /> <span>Headcount tracking</span>
            </>
          ),
          canShow: hasHeadcountSupport,
          links: [
            {
              type: "link",
              display: <span>Roster</span>,
              to: Page.HEADCOUNT_ROSTER,
              activePages: [Page.HEADCOUNT_ROSTER],
            },
            {
              type: "link",
              display: <span>Import</span>,
              to: Page.HEADCOUNT_IMPORT,
              activePages: [Page.HEADCOUNT_IMPORT],
            },
            {
              type: "link",
              display: <span>Insights</span>,
              to: Page.HEADCOUNT_INSIGHTS,
              activePages: [Page.HEADCOUNT_INSIGHTS],
            },
          ],
        },
        {
          type: "link",
          to: Page.DOCUMENTS,
          display: (
            <>
              <DocumentIcon /> <span>Documents</span>
            </>
          ),
          activePages: [
            Page.DOCUMENTS,
            Page.DOCUMENT_DETAILS,
            Page.ADD_DOCUMENTS,
            Page.EDIT_DOCUMENT,
          ],
        },
        {
          type: "menu",
          isOpen: false,
          hideIfEmpty: true,
          display: (
            <>
              <SettingsIcon /> <span>Settings</span>
            </>
          ),
          links: [
            {
              type: "link",
              activePages: [
                Page.MANAGE_DEPARTMENTS,
                Page.MANAGE_BUSINESS_UNITS,
                Page.LOCATIONS,
                Page.SPEND_CATEGORIES,
                Page.MANAGE_GROUPS,
                Page.MANAGE_GROUP,
              ],
              to: Page.MANAGE_DEPARTMENTS,
              display: <span>Company</span>,
              canShow: ability.can("manage", "Settings"),
            },
            {
              type: "link",
              activePages: [
                Page.MEMBERS,
                Page.MEMBER_DETAILS,
                Page.EDIT_MEMBER,
              ],
              to: Page.MEMBERS,
              display: <span>Members</span>,
              canShow:
                ability.can("manage", "Settings") ||
                ability.can("manage", "UsersInDepartmentSettings") ||
                hasUserAdminAccess,
            },
            {
              type: "link",
              activePages: [Page.NOTIFICATION_SETTINGS],
              canShow: hasAdminAccess,
              display: (
                <>
                  <span>Notifications</span>
                </>
              ),
              to: Page.NOTIFICATION_SETTINGS,
            },
            {
              type: "link",
              activePages: [Page.VENDOR_MANAGEMENT_SETTINGS],
              to: Page.VENDOR_MANAGEMENT_SETTINGS,
              display: <span>Vendor management</span>,
              canShow: ability.can("manage", "Settings"),
            },
            {
              type: "link",
              activePages: [Page.SOURCING_SETTINGS],
              to: Page.SOURCING_SETTINGS,
              display: <span>Sourcing</span>,
              canShow: enableSourcing,
            },
            {
              type: "link",
              activePages: [
                Page.SPEND_RULE,
                Page.SPEND_RULES,
                Page.SPEND_RULES_SIMULATE,
                Page.SPEND_RULE_NEW_CUSTOM_FIELDS,
              ],
              to: Page.APPROVALS,
              display: <span>Spend requests</span>,
              canShow: ability.can("manage", "Settings") && enableSpendRequests,
            },
            {
              type: "link",
              activePages: [Page.INTAKE_REQUEST_SETTINGS],
              to: Page.INTAKE_REQUEST_SETTINGS,
              display: <span>Request configuration</span>,
              canShow:
                (ability.can("manage", "Settings") && enableIntakeRequests) ||
                isRequestCategoryOwner,
            },
            {
              type: "link",
              activePages: [
                Page.MANAGE_ACTUALS_MAPPINGS,
                Page.MANAGE_ACTUALS_MAPPINGS_ERP_EXPENSE_ACCOUNTS,
                Page.MANAGE_ACTUALS_MAPPINGS_ERP_REVENUE_ACCOUNTS,
                Page.MANAGE_ACTUALS_MAPPINGS_ERP_DEPARTMENTS,
                Page.MANAGE_ACTUALS_MAPPINGS_ERP_VENDORS,
                Page.MANAGE_PLANS_MAPPINGS,
                Page.MANAGE_PLANS_MAPPINGS_DEPARTMENTS,
                Page.MANAGE_PLANS_MAPPINGS_SPEND_CATEGORIES,
                Page.MANAGE_INTEGRATIONS_MAPPINGS,
              ],
              to: Page.MANAGE_ACTUALS_MAPPINGS,
              display: <span>Manage mappings</span>,
              canShow: ability.can("manage", "Plan Tracking"),
            },
            {
              type: "link",
              activePages: [
                Page.MANAGE_PAYMENTS_SETTINGS,
                Page.MANAGE_PAYMENTS_BILL_RULE,
                Page.MANAGE_PAYMENTS_BILL_RULES,
              ],
              to: Page.MANAGE_PAYMENTS_SETTINGS,
              display: <span>Payments</span>,
              canShow: hasPaymentsSettingsAccess,
            },
            {
              type: "link",
              activePages: [Page.MANAGE_INTEGRATIONS],
              to: Page.MANAGE_INTEGRATIONS,
              display: <span>Integrations</span>,
              canShow: ability.can("manage", "Integrations"),
            },
            {
              type: "link",
              activePages: [Page.MANAGE_API_KEYS],
              to: Page.MANAGE_API_KEYS,
              display: <span>API keys</span>,
              canShow: hasAdminAccess,
            },
          ],
        },
      ]);
    }
  }, [
    ability,
    canSeeFinancialAnalysis,
    hasErpClassifications,
    hasHeadcountSupport,
    hasPaymentsSupport,
    isVendorPortal,
    getFlag,
    enableIntakeRequests,
    enableSpendRequests,
    paymentsEnabled,
    enableVendorIntake,
    hasVendorAdminAccess,
    hasAdminAccess,
    hasSpendRequestAccess,
    hasPaymentsSettingsAccess,
    enableNetsuitePurchaseOrder,
    showSearch,
    hasSuperViewerAccess,
    enableSourcing,
    enablePaymentAccounting,
    hasUserAdminAccess,
    isRequestCategoryOwner,
  ]);

  const onMenuToggle = (index: number) => {
    setNavItems((prev) => {
      return prev.map((item, idx) => {
        if (idx === index && item.type === "menu") {
          return {
            ...item,
            isOpen: !item.isOpen,
          };
        }
        return item;
      });
    });
  };

  // if the current path is in a menu, keep that menu open
  const navItemsWithMenuWithActivePageOpen = useMemo(() => {
    return navItems.map((navItem) => {
      if (navItem.type === "link") return navItem;
      if (navItem.type === "function") return navItem;

      const menuHasActivePage = navItem.links.some((link) =>
        link.activePages.some((page) => pathIsPage(path, page)),
      );

      if (menuHasActivePage) {
        return {
          ...navItem,
          isOpen: true,
        };
      }

      return navItem;
    });
  }, [navItems, path]);

  return (
    <navContext.Provider
      value={{
        navItems: navItemsWithMenuWithActivePageOpen,
        path: pathWithoutTrailingSlash,
        onMenuToggle,
        showSearch,
        setShowSearch,
      }}
    >
      {children}
    </navContext.Provider>
  );
};
